import { useState } from 'react';
import type { FormEvent } from 'react';
import { CheckCircle2 } from 'lucide-react';

interface FormState {
  name: string;
  company: string;
  email: string;
  phone: string;
  message: string;
  website: string;
}

type SubmitStatus = 'idle' | 'sending' | 'sent' | 'error';

const EMPTY: FormState = {
  name: '',
  company: '',
  email: '',
  phone: '',
  message: '',
  website: '',
};

const labelClass = 'mb-1.5 block font-ui text-xs font-semibold text-fg-secondary';
const inputClass =
  'h-11 w-full rounded-sm border border-line bg-card px-3 font-body text-sm text-fg-secondary outline-none transition-colors placeholder:text-fg-muted/70 focus:border-action';

export function PartnerForm() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [error, setError] = useState('');

  const set = (key: keyof FormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((current) => ({ ...current, [key]: event.target.value }));
      if (status === 'error') {
        setStatus('idle');
        setError('');
      }
    };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('sending');
    setError('');

    try {
      const response = await fetch('/api/partner-enquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      const result = (await response.json().catch(() => null)) as { message?: string } | null;

      if (!response.ok) {
        throw new Error(result?.message ?? 'We could not send your enquiry. Please try again.');
      }

      setStatus('sent');
    } catch (submitError) {
      setStatus('error');
      setError(
        submitError instanceof Error
          ? submitError.message
          : 'We could not send your enquiry. Please try again.',
      );
    }
  };

  return (
    <section id="partner" className="bg-brand py-section">
      <div className="mx-auto grid max-w-content items-center gap-12 px-6 md:grid-cols-2 md:gap-16 md:px-16">
        <div>
          <p className="font-ui text-[0.8rem] font-semibold uppercase tracking-[0.12em] text-white/85">
            Become a partner
          </p>
          <h2 className="mt-4 font-display text-[2.5rem] font-bold leading-[1.1] tracking-tight text-white">
            Partner with FMC Events
          </h2>
          <p className="mt-4 max-w-md font-body text-lg leading-relaxed text-white">
            Align your brand with the UK's most influential dental conferences and awards.
          </p>
          <p className="mt-6 font-body text-sm text-white/80">
            Our team responds to every enquiry within two working days.
          </p>
        </div>

        <div className="rounded-lg bg-card p-6 sm:p-8" style={{ boxShadow: 'var(--app-shadow-soft)' }}>
          {status === 'sent' ? (
            <div className="flex min-h-[26rem] flex-col items-center justify-center text-center" role="status">
              <CheckCircle2 size={44} strokeWidth={1.5} className="text-action" aria-hidden="true" />
              <h3 className="mt-4 font-display text-2xl font-bold text-fg-primary">Enquiry received</h3>
              <p className="mt-2 max-w-sm font-body text-sm leading-relaxed text-fg-muted">
                Thanks {form.name}. The FMC Events partnerships team will be in touch within two working days.
              </p>
              <button
                type="button"
                onClick={() => {
                  setForm(EMPTY);
                  setStatus('idle');
                }}
                className="mt-6 font-ui text-sm font-semibold text-action transition-colors hover:text-action-hover"
              >
                Submit another enquiry
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate={false}>
              <div className="sr-only-field" aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  id="website"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={set('website')}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className={labelClass} htmlFor="name">Name</label>
                  <input
                    id="name"
                    name="name"
                    autoComplete="name"
                    required
                    maxLength={120}
                    value={form.name}
                    onChange={set('name')}
                    placeholder="Your name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="company">Company</label>
                  <input
                    id="company"
                    name="company"
                    autoComplete="organization"
                    required
                    maxLength={160}
                    value={form.company}
                    onChange={set('company')}
                    placeholder="Company name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength={254}
                    value={form.email}
                    onChange={set('email')}
                    placeholder="you@company.com"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass} htmlFor="phone">Phone <span className="font-normal text-fg-muted">(optional)</span></label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    maxLength={40}
                    value={form.phone}
                    onChange={set('phone')}
                    placeholder="+44"
                    className={inputClass}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className={labelClass} htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  required
                  maxLength={3000}
                  value={form.message}
                  onChange={set('message')}
                  placeholder="Tell us about your partnership goals…"
                  className="w-full resize-y rounded-sm border border-line bg-card p-3 font-body text-sm text-fg-secondary outline-none transition-colors placeholder:text-fg-muted/70 focus:border-action"
                />
              </div>

              {status === 'error' && (
                <p className="mt-4 rounded-sm border border-red-200 bg-red-50 px-3 py-2 font-body text-sm text-red-800" role="alert">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="mt-5 w-full rounded-sm bg-action py-3.5 font-ui text-[0.95rem] font-semibold text-action-foreground transition-colors hover:bg-action-hover disabled:cursor-wait disabled:opacity-65"
              >
                {status === 'sending' ? 'Sending…' : 'Submit enquiry'}
              </button>
              <p className="mt-3 text-center font-body text-xs leading-relaxed text-fg-muted">
                We use your details to respond to this enquiry. See the{' '}
                <a
                  href="https://www.fmc.co.uk/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-action"
                >
                  FMC privacy policy
                </a>.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

